import React from "react";
import LineChart from "@cloudscape-design/components/line-chart";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import * as d3 from "d3";

const EndpointGraph = ({ data, duration }) => {
  // Prepare the data for the LineChart component
  const series = data.map((endpoint) => {
    const formattedData = endpoint.averagePerDayData.map((d) => ({
      x: new Date(d.timeInterval),
      y: d.avgAvailability,
    }));

    if (duration !== "7days") {
      // Group data by week and calculate weekly average
      const weeklyData = d3
        .groups(formattedData, (d) => d3.timeWeek(d.x))
        .map(([week, values]) => ({
          x: week,
          y: d3.mean(values, (d) => d.y),
        }));

      return {
        title: endpoint.endpointName,
        type: "line",
        data: weeklyData,
        valueFormatter: (value) => `${value.toFixed(2)}%`,
      };
    }

    return {
      title: endpoint.endpointName,
      type: "line",
      data: formattedData,
      valueFormatter: (value) => `${value.toFixed(2)}%`,
    };
  });

  // Determine the xDomain based on the duration
  const now = new Date();
  let xDomain;
  let xTicks;

  if (duration === "7days") {
    const last7Days = d3.timeDay.range(
      d3.timeDay.offset(now, -6),
      d3.timeDay.offset(now, 1)
    );
    xDomain = [last7Days[0], last7Days[last7Days.length - 1]];
    // For 7 days, we'll use explicit ticks to prevent duplication
    xTicks = last7Days.map((d) => d);
  } else if (duration === "1month") {
    const lastMonth = d3.timeWeek.range(
      d3.timeWeek.offset(now, -4),
      d3.timeWeek.offset(now, 1)
    );
    xDomain = [lastMonth[0], lastMonth[lastMonth.length - 1]];
    xTicks = lastMonth;
  } else if (duration === "3months") {
    const last3Months = d3.timeWeek.range(
      d3.timeWeek.offset(now, -12),
      d3.timeWeek.offset(now, 1)
    );
    xDomain = [last3Months[0], last3Months[last3Months.length - 1]];
    xTicks = d3.timeWeek.range(xDomain[0], xDomain[1], 2);
  }

  // Custom tick formatter to prevent duplication
  const xTickFormatter = (date) => {
    if (duration === "7days") {
      // Use a more compact format for 7-day view
      const formatDate = d3.timeFormat("%b %d");
      const tickDate = formatDate(date);

      // Store the last formatted date to prevent duplication
      if (xTickFormatter.lastDate === tickDate) {
        return "";
      }
      xTickFormatter.lastDate = tickDate;
      return tickDate;
    } else {
      return d3.timeFormat("%b %d")(date);
    }
  };

  // Filter data to include only the relevant weeks
  const filterRelevantWeeks = (data, startDate) => {
    return data.filter((d) => d.x >= startDate && d.x <= now);
  };

  const updatedSeries = series.map((s) => {
    if (duration === "7days") {
      const last7Days = d3.timeDay.range(
        d3.timeDay.offset(now, -6),
        d3.timeDay.offset(now, 1)
      );
      return {
        ...s,
        data: filterRelevantWeeks(s.data, last7Days[0]),
      };
    } else if (duration === "1month") {
      const lastMonth = d3.timeWeek.range(
        d3.timeWeek.offset(now, -4),
        d3.timeWeek.offset(now, 1)
      );
      return {
        ...s,
        data: filterRelevantWeeks(s.data, lastMonth[0]),
      };
    } else if (duration === "3months") {
      const last3Months = d3.timeWeek.range(
        d3.timeWeek.offset(now, -12),
        d3.timeWeek.offset(now, 1)
      );
      return {
        ...s,
        data: filterRelevantWeeks(s.data, last3Months[0]),
      };
    }
    return s;
  });

  return (
    <div className="mt-12 px-4 w-full mx-auto">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        <div className="p-6">
          <h3 className="text-2xl font-bold text-[#003366] mb-6 text-center">
            Endpoint Success Rate Analytics
          </h3>

          <div className="bg-[#003366]/5 p-4 rounded-md mb-6">
            <div className="text-sm text-[#003366] flex items-center justify-between">
              <span>
                Data Duration:{" "}
                {duration === "7days"
                  ? "Past 7 Days"
                  : duration === "1month"
                    ? "Past Month"
                    : "Past 3 Months"}
              </span>
              <span className="font-medium">UTC Time Zone</span>
            </div>
          </div>

          <div className="relative">
            <LineChart
              series={updatedSeries}
              xDomain={xDomain}
              yDomain={[0, 100]}
              xTicks={xTicks}
              i18nStrings={{
                xTickFormatter,
                yTickFormatter: (value) => `${value.toFixed(0)}%`,
              }}
              emphasizeBaselineAxis={true}
              height={300}
              hideFilter
              xScaleType="time"
              xTitle="Time Period"
              yTitle="Success Rate (%)"
              detailPopoverSeriesContent={({ series, x, y }) => ({
                key: (
                  <span className="text-[#003366] hover:text-[#004488]">
                    {series.title}
                  </span>
                ),
                value: <span className="font-medium">{y.toFixed(2)}%</span>,
              })}
              empty={
                data.length === 0 ? (
                  <>
                    <div className="flex  justify-center items-center">
                      <div className="w-12 h-12 m-10 border-4 border-blue-200 border-t-[#003366] rounded-full animate-spin"></div>
                    </div>
                  </>
                ) : (
                  <Box textAlign="center" color="inherit" className="py-12">
                    <div className="text-[#003366]">
                      <svg
                        className="w-12 h-12 mx-auto mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 12H4"
                        />
                      </svg>
                      <p className="text-lg font-semibold">No Data Available</p>
                      <p className="mt-2">
                        There is currently no data to display
                      </p>
                    </div>
                  </Box>
                )
              }
              noMatch={
                <Box textAlign="center" color="inherit" className="py-12">
                  <div className="text-[#003366]">
                    <p className="text-lg font-semibold">No Matching Data</p>
                    <p className="mt-2 mb-4">
                      No data matches the current filter criteria
                    </p>
                    <Button className="bg-[#003366] text-white px-4 py-2 rounded hover:bg-[#004488]">
                      Clear Filter
                    </Button>
                  </div>
                </Box>
              }
            />
          </div>

          <div className="mt-6 text-sm text-[#003366] text-center">
            Click on the legend items to toggle endpoint visibility
          </div>
        </div>
      </div>
    </div>
  );
};

export default EndpointGraph;
